/**
 * AlertSettingsPanel — the canonical Alerts & Notifications home (#232).
 *
 * One shared panel, consumed by the full Settings page and (later) #234's
 * compact Position Tracker pull-up. It replaces two drifting panels: the live
 * `views/Settings/SoundSettingsPanel` (five of eleven sounds, no per-alert
 * volume, stale after any sibling-tab write) and the unimported
 * `organisms/SoundSettingsPanel` (advertised five dormant tones with no emit
 * site, and previewed a dormant tone for its one queue row).
 *
 * Rows are DERIVED from `SOUND_ALERT_CHANNEL_IDS` — active AND audible. Not
 * `ACTIVE_ALERT_CHANNEL_IDS`: since #233 that includes the flash-only velocity
 * channels, which have no tone to preview and no volume to scale. Adding a
 * sounding channel to the registry adds its row here with no edit to this file.
 *
 * Sound only, by scope. #233's delivery thresholds and flash toggles belong to
 * #234's pull-up; see `docs/260731AlertSettingsUIPlan.md` for why. The switch
 * and the category labels are shared with that pull-up via
 * `molecules/alertControls` so the two surfaces cannot drift.
 *
 * #768 adds the sound-readiness check at the top: it resumes the shared
 * AudioContext from its own click and then reports, truthfully, whether an alert
 * would be audible — ready, muted (master or channel), or browser-blocked with
 * recovery guidance. It changes no preference. The decision lives in the pure
 * `describeSoundCheck` below, which returns the message and the play decision
 * together so the panel cannot claim a sound it never rang.
 */

import { useRef, useState } from 'react';
import { Volume2, Play } from 'lucide-react';
import { AlertSwitch, CATEGORY_LABELS } from '@/components/molecules/alertControls';
import { useAlertPreferences } from '@/hooks/ui/useAlertPreferences';
import { readAlertPreferences } from '@/lib/alerts/alertPreferences';
import { ensureAudioReady, reportSynthAttemptHealth, type AudioReadiness } from '@/lib/audio/audioContext';
import { tryFillSoundNow } from '@/lib/audio/tradeAudio';
import {
  SOUND_ALERT_CHANNEL_IDS,
  ALERT_REGISTRY,
  type AlertChannelId,
  type AlertCategory,
  type AlertChannelSettings,
} from '@/lib/alerts/registry';
import { DIVERGENCE_THRESHOLD_CENTS } from '@/lib/divergence/polyKalshiDivergence';
import { ALERT_PREVIEW_TONES } from './alertPreview';

/**
 * Channels that modify another channel's tone rather than owning one. The
 * live-game entry gates the divergence chime: its emit site reads this channel
 * as a boolean and rings `playDivergenceTone(divergenceCh.volume)`. It gets a
 * toggle, and deliberately no volume slider or preview — it owns neither.
 */
const NESTED_UNDER: Partial<Record<AlertChannelId, AlertChannelId>> = {
  divergenceLiveGame: 'divergence',
};

/**
 * Channels whose tone only fires while another channel's is on, because both
 * emit sites sit inside the same `if (isRecent && snd.tradeSoundEnabled)` guard
 * in `accountStore.processFill`. Surfaced as copy rather than a disabled
 * control: the issue requires an enabled toggle on every active sound row, and
 * disabling would stop a trader configuring one ahead of turning fills back on.
 */
const REQUIRES: Partial<Record<AlertChannelId, AlertChannelId>> = {
  firstFill: 'fill',
  whaleFill: 'fill',
};

const TOP_LEVEL_IDS = SOUND_ALERT_CHANNEL_IDS.filter((id) => !NESTED_UNDER[id]);

/** Group order follows the registry's own order, never a hand-written list. */
const CATEGORY_ORDER: AlertCategory[] = TOP_LEVEL_IDS.reduce<AlertCategory[]>((acc, id) => {
  const c = ALERT_REGISTRY[id].category;
  return acc.includes(c) ? acc : [...acc, c];
}, []);

const pct = (v: number) => `${Math.round(v * 100)}%`;

// ── Sound readiness check (#768) ────────────────────────────────────

/**
 * The channel whose tone the readiness check rings.
 *
 * `fill` deliberately, and named rather than derived: it is the first entry in
 * `ALERT_CHANNEL_IDS`, the tone a trader hears most, and the one whose switch
 * also gates first-fill and whale. Deriving it as "whatever is first in the
 * registry" would let a reorder silently change which sound this button plays.
 */
const TEST_CHANNEL = 'fill' as const;

/**
 * Below this a tone is inaudible by the emitter's own rule: `playSynthSound`
 * returns early on `master < 0.001`, and scales every voice by the channel
 * multiplier before applying the same floor per voice. Both sliders move in
 * whole percent, so in practice this is exactly "set to 0%".
 */
const AUDIBLE_FLOOR = 0.001;

export interface SoundCheckOutcome {
  severity: 'ok' | 'muted' | 'error';
  message: string;
  /**
   * Whether the caller should actually ring the tone.
   *
   * The decision to play and the claim about playing are returned together, on
   * purpose: they cannot drift into a panel that says "you just heard that"
   * without having rung anything.
   */
  play: boolean;
}

/**
 * Turn a readiness probe plus the current preferences into what the panel says.
 *
 * Pure, exported, and tested directly — the ordering below IS the contract:
 *
 * 1. A browser that cannot play wins over everything. It is invisible in this
 *    panel, whereas a muted slider is sitting right there reading 0%.
 * 2. Then the mute reasons, most global first, because master zero makes every
 *    row's own setting moot.
 * 3. Only a genuinely audible path returns `play`.
 *
 * The result NEVER claims a sound was heard on a path that produces silence.
 * That is the whole point of the issue: the previous surface — a 6px dot in the
 * Position Sidebar header — could not tell these apart, and `playSynthSound`
 * reports `ok` for two of them.
 */
export function describeSoundCheck(
  readiness: AudioReadiness,
  masterVolume: number,
  channel: AlertChannelSettings,
  channelLabel: string
): SoundCheckOutcome {
  if (readiness === 'unavailable') {
    return {
      severity: 'error',
      message:
        'This tab could not open an audio channel, so no alert can sound here. Reloading the page usually restores it.',
      play: false,
    };
  }
  if (readiness === 'blocked') {
    // The menu labels are taken from Apple's and Google's own current
    // documentation, not from memory. Safari calls the per-site panel
    // "Website Settings" — reached from the page menu in the Smart Search
    // field — NOT "Settings for This Website", which is the older name and was
    // what an earlier draft of this string said. Wrong instructions on this
    // surface are a shipped bug: it is read by a trader who already cannot
    // hear their fills, at the moment they can least afford a wild goose chase.
    return {
      severity: 'error',
      message:
        'Your browser is still holding audio back for this tab, so alerts cannot sound yet. ' +
        'In Safari, click the page menu in the address field, choose Website Settings, ' +
        'and set Auto-Play to Allow All Auto-Play. ' +
        'In Chrome, click the icon next to the web address and set Sound to Allow. ' +
        'Then click Test sound again.',
      play: false,
    };
  }
  if (masterVolume < AUDIBLE_FLOOR) {
    return {
      severity: 'muted',
      message:
        'Your browser is ready to play sound, but master volume is 0% — every alert is silent until you raise it.',
      play: false,
    };
  }
  if (!channel.soundEnabled) {
    return {
      severity: 'muted',
      message: `Your browser is ready to play sound, but the ${channelLabel} alert is switched off, so this check has nothing to ring.`,
      play: false,
    };
  }
  if (channel.volume < AUDIBLE_FLOOR) {
    return {
      severity: 'muted',
      message: `Your browser is ready to play sound, but ${channelLabel} volume is 0%, so this check has nothing to ring.`,
      play: false,
    };
  }
  // The only claim on this panel that cannot be VERIFIED. Every other state is
  // derived from something readable — context state, master volume, the channel
  // switch — but neither Web Audio nor any other browser API exposes OS volume
  // or a muted tab, so a healthy context proves the tone was SENT, never that it
  // was heard. On an issue about a UI that could not tell silence causes apart,
  // an unfalsifiable success claim is the same failure in a new place, so the
  // one thing left that can still swallow it is named rather than implied.
  return {
    severity: 'ok',
    message:
      `Sound is working — that was the ${channelLabel} alert at its current volume. ` +
      'If you did not hear it, check your system volume and that this tab is not muted.',
    play: true,
  };
}

/**
 * The concrete level that fires this channel's TONE, in the trader's own terms.
 * The issue asks for copy that says what event triggers a sound, and "the
 * warning level" names a variable rather than an event.
 *
 * Which number is the true one differs per channel, and getting it wrong would
 * put a false claim on screen:
 *
 * - The three QUEUE channels genuinely sound at the user's own
 *   `deliveryThreshold` — it feeds `stepQueueDelivery`, and
 *   `selectQueueSoundChannel` picks the tone out of the events that produces.
 *   Falling back to `meta.defaults.deliveryThreshold` mirrors the emit site's
 *   own `?? QUEUE_*_THRESHOLD` fallback. Note the two are pinned equal by
 *   `deliveryInvariants.test.ts` rather than shared: it asserts the queue
 *   defaults as LITERALS ("the product contract... a change to either must be
 *   deliberate"), while fell-behind/velocity/divergence bind to their constants
 *   by import. So the fallback path is only reachable on a stored null, and a
 *   deliberate change to a tracker literal must update the registry too.
 * - DIVERGENCE does NOT. Its emit site runs two latches: a canonical one at a
 *   fixed 2c that drives the CHIME, and a personal one at `deliveryThreshold`
 *   that drives only the FLASH. This panel controls the chime, so it must quote
 *   the constant — a trader who raised their personal threshold to 10c would
 *   otherwise read that the chime fires at 10c when it still fires at 2c.
 *
 * Channels with no verified tone-level return null: no number beats a wrong one,
 * so a future sounding channel with a threshold simply shows no claim until
 * someone states its real one here.
 */
function toneTriggerDetail(
  id: AlertChannelId,
  channels: Record<AlertChannelId, AlertChannelSettings>
): string | null {
  const levelOf = (channelId: AlertChannelId) =>
    channels[channelId].deliveryThreshold ?? ALERT_REGISTRY[channelId].defaults.deliveryThreshold;
  const fmt = (n: number | null) => (n === null ? null : n.toLocaleString('en-US'));

  switch (id) {
    // The queue pair is ORDERED for display, exactly as `stepQueueDelivery`
    // orders it (`criticalAt = min`, `warningAt = max`). Storage is lossless by
    // design and #233 repairs an inverted pair at each consumption site rather
    // than on read, so a stale cross-tab write can leave warning below critical
    // in storage. Printing the raw values would then tell the trader the tones
    // fire at levels they do not — the panel is a consumer, and the reviewer's
    // standing requirement on #233 is that no consumer observes an inverted
    // pair. At any valid pair min/max are identities, so this is a no-op.
    case 'queueWarning':
    case 'queueCritical': {
      const warn = levelOf('queueWarning');
      const crit = levelOf('queueCritical');
      if (warn === null || crit === null) return null;
      const level = id === 'queueCritical' ? Math.min(crit, warn) : Math.max(crit, warn);
      return `Sounds when the queue ahead of you drops to ${fmt(level)}.`;
    }
    // No ordered sibling — a standalone bound.
    case 'fellBehind': {
      const level = fmt(levelOf('fellBehind'));
      return level && `Sounds when better-priced size ahead reaches ${level}.`;
    }
    // Deliberately the CONSTANT, not this channel's deliveryThreshold: that
    // personal value drives the flash latch only.
    case 'divergence':
      return `Sounds at a fixed ${DIVERGENCE_THRESHOLD_CENTS}¢ gap.`;
    default:
      return null;
  }
}

export function AlertSettingsPanel() {
  const { prefs, setMasterVolume, setMinFillContracts, setChannelSoundEnabled, setChannelVolume } =
    useAlertPreferences();

  const [check, setCheck] = useState<SoundCheckOutcome | null>(null);
  const [testing, setTesting] = useState(false);
  // Only the newest click may report. The probe can wait up to a second on a
  // blocked browser, which is long enough for an impatient second click to
  // overtake the first and print a stale verdict.
  const runIdRef = useRef(0);

  const runSoundCheck = async () => {
    const runId = ++runIdRef.current;
    setTesting(true);
    setCheck(null);

    // Resume from THIS click, before any tone is played. The document-level
    // warmup listener is not a substitute: measured in Chrome, at the moment a
    // click handler runs the context is still 'suspended' — the warmup's own
    // resume only settles later, after every synchronous handler has returned.
    const readiness = await ensureAudioReady();
    if (runIdRef.current !== runId) return;

    // Read preferences AFTER the await, not from the render closure: the probe
    // can sit for a second, and the setting that matters is the one in force
    // when the tone actually rings. Same reasoning as `processFill`, which
    // reads its volume inside the queued closure rather than at selection time.
    const current = readAlertPreferences();
    const channel = current.channels[TEST_CHANNEL];
    let outcome = describeSoundCheck(
      readiness,
      current.masterVolume,
      channel,
      ALERT_REGISTRY[TEST_CHANNEL].label
    );

    // Use the same Fill voices as real alerts, but inspect actual scheduling
    // before showing success. Readiness alone cannot prove the tone started.
    if (outcome.play) {
      const attempt = tryFillSoundNow(TEST_CHANNEL, channel.volume, () => runIdRef.current === runId);
      reportSynthAttemptHealth(attempt);
      if (attempt.kind === 'partial') {
        outcome = { severity: 'error', play: false,
          message: 'The Fill alert could not finish. You may have heard part of it. Reload this page and test again.' };
      } else if (attempt.kind === 'not-started') {
        if (attempt.reason === 'fenced') return;
        outcome = attempt.reason === 'blocked'
          ? describeSoundCheck('blocked', current.masterVolume, channel, ALERT_REGISTRY[TEST_CHANNEL].label)
          : attempt.reason === 'muted'
          ? { severity: 'muted', play: false, message: 'The Fill alert is too quiet to play. Raise the master or Fill volume and test again.' }
          : { severity: 'error', play: false, message: 'Your browser could not play the Fill alert. Reload this page and test again.' };
      }
    }

    setCheck(outcome);
    setTesting(false);
  };

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      <div className="mb-1 flex items-center gap-2">
        <Volume2 size={18} className="text-muted-foreground" />
        <h2 className="text-base font-medium">Alerts &amp; Notifications</h2>
      </div>
      <p className="text-muted-foreground mb-5 text-xs">
        Every sound the app plays. Saved locally to this browser.
      </p>

      {/* ── Sound readiness check (#768) ────────────────────────────── */}
      <div className="border-border mb-5 rounded-md border p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="text-foreground text-xs font-medium">Check my sound</span>
            <p className="text-muted-foreground text-xs leading-snug">
              Plays a real {ALERT_REGISTRY[TEST_CHANNEL].label} alert and reports what happened.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void runSoundCheck()}
            disabled={testing}
            aria-busy={testing}
            className="focus:ring-primary/50 focus:ring-offset-background flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs text-blue-500 transition-colors hover:bg-blue-500/10 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50 dark:text-blue-400"
          >
            <Play size={11} aria-hidden="true" />
            {testing ? 'Testing…' : 'Test sound'}
          </button>
        </div>
        {/* One live region for every outcome, mounted while empty so a verdict
            announces as an update rather than as a new subtree — the same call
            `LiveHotkeyRemapPanel` and `EventStateWarning` make. Idle it carries
            the guidance, so the space is never wasted. */}
        <p
          role="status"
          aria-live="polite"
          className={`mt-2 text-xs leading-snug ${
            check?.severity === 'error'
              ? 'text-negative'
              : check?.severity === 'muted'
                ? // Theme-aware, matching the repo's established warning pair: bare
                  // `amber-400` is legible on the dark card but washes out on the
                  // light one, where this row still has to be readable.
                  'text-amber-600 dark:text-amber-400'
                : check?.severity === 'ok'
                  ? 'text-positive'
                  : 'text-muted-foreground'
          }`}
        >
          {check?.message ??
            (testing
              ? 'Waking up audio…'
              : 'Sound settings are saved per browser, so this checks the browser you are in right now.')}
        </p>
      </div>

      {/* ── Master controls ─────────────────────────────────────────── */}
      <div className="mb-5">
        <label
          htmlFor="alerts-master-volume"
          className="text-muted-foreground mb-1.5 block text-xs font-medium"
        >
          Master volume — {pct(prefs.masterVolume)}
        </label>
        <input
          id="alerts-master-volume"
          type="range"
          min={0}
          max={100}
          value={Math.round(prefs.masterVolume * 100)}
          onChange={(e) => setMasterVolume(Number(e.target.value) / 100)}
          // The visible label embeds the live percentage; without this the
          // control's accessible NAME would change on every drag. The value is
          // already conveyed by aria-valuenow/valuetext.
          aria-label="Master volume"
          aria-valuetext={pct(prefs.masterVolume)}
          className="bg-secondary h-1.5 w-full cursor-pointer appearance-none rounded-full accent-blue-500"
        />
        <p className="text-muted-foreground mt-1 text-xs leading-snug">
          Scales every sound below. Each alert&apos;s own slider multiplies with this one, so an
          alert at 50% of a 50% master plays at 25%. Set to 0% to mute everything.
        </p>
      </div>

      {/* ── Sound rows, grouped by registry category ────────────────── */}
      <div className="space-y-5">
        {CATEGORY_ORDER.map((category) => (
          <section key={category} aria-label={CATEGORY_LABELS[category]}>
            <h3 className="text-muted-foreground mb-2 text-xs font-medium tracking-wider uppercase">
              {CATEGORY_LABELS[category]}
            </h3>
            <div className="space-y-2">
              {TOP_LEVEL_IDS.filter((id) => ALERT_REGISTRY[id].category === category).map((id) => (
                <AlertRow
                  key={id}
                  id={id}
                  masterVolume={prefs.masterVolume}
                  settings={prefs.channels[id]}
                  channels={prefs.channels}
                  onToggle={setChannelSoundEnabled}
                  onVolume={setChannelVolume}
                  nested={SOUND_ALERT_CHANNEL_IDS.filter(
                    (child) => NESTED_UNDER[child] === id
                  ).map((child) => (
                    <NestedAlertRow
                      key={child}
                      id={child}
                      enabled={prefs.channels[child].soundEnabled}
                      onToggle={setChannelSoundEnabled}
                    />
                  ))}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* ── Minimum fill size ───────────────────────────────────────── */}
      <div className="mt-5">
        <label
          htmlFor="alerts-min-fill"
          className="text-muted-foreground mb-1.5 block text-xs font-medium"
        >
          Minimum fill contracts — currently {prefs.minFillContracts}
        </label>
        <input
          id="alerts-min-fill"
          type="number"
          min={0}
          aria-label="Minimum fill contracts"
          value={prefs.minFillContracts}
          onChange={(e) => setMinFillContracts(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
          className="bg-card border-border text-foreground w-24 rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
        />
        <p className="text-muted-foreground mt-1 text-xs leading-snug">
          Only play the three Fills sounds when a single fill is at least this many contracts.
          0 hears every fill. Order, queue, cross-venue and boot sounds ignore it — and a
          first fill still rings below the threshold, so a small opening fill is never silent.
        </p>
      </div>
    </div>
  );
}

// ── Rows ────────────────────────────────────────────────────────────

function AlertRow({
  id,
  settings,
  channels,
  masterVolume,
  onToggle,
  onVolume,
  nested,
}: {
  id: AlertChannelId;
  settings: AlertChannelSettings;
  /** The whole map: the queue pair's copy is ordered against its sibling. */
  channels: Record<AlertChannelId, AlertChannelSettings>;
  masterVolume: number;
  onToggle: (id: AlertChannelId, enabled: boolean) => void;
  onVolume: (id: AlertChannelId, volume: number) => void;
  nested: React.ReactNode;
}) {
  const meta = ALERT_REGISTRY[id];
  const preview = ALERT_PREVIEW_TONES[id];
  const requires = REQUIRES[id];
  const trigger = toneTriggerDetail(id, channels);

  return (
    <div className="bg-muted/50 rounded-md px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <AlertSwitch
            checked={settings.soundEnabled}
            label={meta.label}
            kind="sound"
            onChange={(next) => onToggle(id, next)}
          />
          <div className="min-w-0">
            <span className="text-foreground text-xs font-medium">{meta.label}</span>
            <p className="text-muted-foreground text-xs leading-snug">{meta.description}</p>
            {trigger && (
              <p className="text-muted-foreground/80 text-xs leading-snug">{trigger}</p>
            )}
            {requires && (
              <p className="text-muted-foreground/80 text-xs leading-snug italic">
                Only plays while {ALERT_REGISTRY[requires].label} is on.
              </p>
            )}
          </div>
        </div>
        {preview && (
          <button
            type="button"
            onClick={() => preview(settings.volume)}
            aria-label={`Preview ${meta.label} sound`}
            className="focus:ring-primary/50 focus:ring-offset-background flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs text-blue-500 transition-colors hover:bg-blue-500/10 focus:ring-2 focus:ring-offset-2 focus:outline-none dark:text-blue-400"
          >
            <Play size={11} aria-hidden="true" />
            Preview
          </button>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <label
          htmlFor={`alert-volume-${id}`}
          className="text-muted-foreground shrink-0 text-xs"
        >
          Volume
        </label>
        <input
          id={`alert-volume-${id}`}
          type="range"
          min={0}
          max={100}
          value={Math.round(settings.volume * 100)}
          onChange={(e) => onVolume(id, Number(e.target.value) / 100)}
          // The visible label reads "Volume" on every row; the accessible name
          // carries the channel so a screen-reader user can tell them apart.
          aria-label={`${meta.label} volume`}
          aria-valuetext={pct(settings.volume)}
          className="bg-secondary h-1.5 flex-1 cursor-pointer appearance-none rounded-full accent-blue-500"
        />
        {/* Always shown, including at defaults — the issue asks for the CURRENT
            EFFECTIVE DEFAULTS, and a row that hides its effective volume until
            it is tuned does not show them. It repeats across rows at defaults;
            that is the price of the control being honest about what this alert
            will actually play at. */}
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
          {pct(settings.volume)}
          <span className="text-muted-foreground/70">
            {' '}
            · {pct(settings.volume * masterVolume)} effective
          </span>
        </span>
      </div>

      {nested}
    </div>
  );
}

/**
 * A gate on its parent's tone: toggle and copy only. No slider and no preview,
 * because the channel owns no volume and no sound of its own.
 */
function NestedAlertRow({
  id,
  enabled,
  onToggle,
}: {
  id: AlertChannelId;
  enabled: boolean;
  onToggle: (id: AlertChannelId, enabled: boolean) => void;
}) {
  const meta = ALERT_REGISTRY[id];
  return (
    // Indented to sit under the parent's TEXT, not its toggle, with a rule down
    // the left — so it reads as a setting belonging to the row above rather
    // than as the next sound in the list.
    <div className="border-muted-foreground/25 mt-3 ml-3 flex items-start gap-3 border-l pt-0.5 pb-0.5 pl-4">
      <AlertSwitch
        checked={enabled}
        label={meta.label}
        kind="sound"
        onChange={(next) => onToggle(id, next)}
      />
      <div className="min-w-0">
        <span className="text-foreground text-xs font-medium">{meta.label}</span>
        <p className="text-muted-foreground text-xs leading-snug">{meta.description}</p>
      </div>
    </div>
  );
}
